import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import NotFound from '@/pages/NotFound';
import { moneyPageBySlug, relatedMoneyPages, siblingMoneyPages } from '@/data/seo/moneyPages';
import { breadcrumbSchema, faqSchema, serviceSchema, moneyFaqs, offerCatalogSchema } from '@/lib/seoSchema';
import {
  Breadcrumbs, PageHero, AnswerBlock, Section, FaqSection, LinkGrid, CtaBand, EntityFacts, PageWrap, TrustStrip,
} from '@/components/seo/SeoBlocks';
import { LEAD_PRICE, money, PLAN_INCLUDED_LEADS, PLAN_PRICES, PLAN_CODES, planBillingSuffix } from '@/data/constants';

import LiveProjects from '@/components/seo/LiveProjects';

const MoneyPage: React.FC = () => {
  const { slug = '' } = useParams();
  const { lang, path, t } = useI18n();
  const page = moneyPageBySlug(slug);
  if (!page) return <NotFound />;

  const tradeName = lang === 'es' ? page.trade.es : page.trade.en;
  const faqs = moneyFaqs(page.trade.en, page.place, lang);
  const url = `/${lang}/leads/${page.slug}`;
  const h1 = lang === 'es' ? `Leads de ${tradeName} en ${page.place}` : `${page.trade.en} Leads in ${page.place}`;

  const answer =
    lang === 'es'
      ? `Los leads de ${tradeName.toLowerCase()} en ${page.place} son proyectos reales publicados por propietarios y contratistas generales. Cada lead cuesta ${money(LEAD_PRICE)} e incluye alcance, presupuesto y, al desbloquearlo, la ciudad exacta, el código postal, los contactos aprobados y los planos adjuntos.`
      : `${page.trade.en} leads in ${page.place} are real projects posted by homeowners and general contractors. Each lead costs a flat ${money(LEAD_PRICE)} and includes the scope and budget range — unlocking it reveals the exact city, ZIP, approved contact details and any attached plan documents.`;

  const related = relatedMoneyPages(page).map((m) => ({ label: `${m.trade.en} Leads ${m.place}`, to: path(`/leads/${m.slug}`) }));
  const siblings = siblingMoneyPages(page).map((m) => ({ label: `${m.trade.en} Leads ${m.place}`, to: path(`/leads/${m.slug}`) }));
  const locationLinks = [
    ...(page.citySlug ? [{ label: `${page.place} construction projects`, to: path(`/california/${page.citySlug}`) }] : []),
    ...(page.countySlug ? [{ label: `${page.countySlug.replace(/-county$/, '').replace(/-/g, ' ')} County projects`, to: path(`/california/counties/${page.countySlug}`) }] : []),
    { label: `${page.trade.en} trade overview`, to: path(`/trades/${page.trade.slug}`) },
    { label: 'California construction hub', to: path('/california') },
  ];

  return (
    <>
      <Seo
        title={h1}
        description={
          lang === 'es'
            ? `Compre leads de ${tradeName.toLowerCase()} en ${page.place} por ${money(LEAD_PRICE)}. Proyectos verificados, precio fijo, sin contratos ocultos.`
            : `Buy ${page.trade.en.toLowerCase()} leads in ${page.place} for a flat ${money(LEAD_PRICE)}. Real posted projects, one price, no hidden bidding fees.`
        }
        schema={[
          breadcrumbSchema([
            { name: 'Home', url: `/${lang}` },
            { name: 'Construction Leads', url: `/${lang}/leads` },
            { name: h1, url },
          ]),
          serviceSchema({ trade: `${page.trade.en} leads`, area: page.place, description: answer, url }),
          offerCatalogSchema(),
          faqSchema(faqs),
        ]}
      />

      <PageHero eyebrow={t('seo.moneyEyebrow')} title={h1} intro={answer}>
        <Link to={path('/register')} className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold hover:bg-orange-600">
          {t('seo.ctaSubBtn')}
        </Link>
        <Link to={path('/pricing')} className="rounded-xl border border-white/20 px-5 py-3 text-sm font-bold hover:bg-white/10">
          {t('nav.pricing')}
        </Link>
      </PageHero>

      <PageWrap>
        <Breadcrumbs
          items={[
            { name: t('nav.home'), url: path('/') },
            { name: t('seo.leadsHub'), url: path('/leads') },
            { name: h1 },
          ]}
        />

        <AnswerBlock question={faqs[0].q} answer={faqs[0].a} />

        <EntityFacts
          rows={[
            { k: t('seo.factTrade'), v: tradeName },
            { k: t('seo.factArea'), v: page.place },
            { k: t('seo.factPrice'), v: money(LEAD_PRICE) },
            { k: t('seo.factIncluded'), v: `${PLAN_INCLUDED_LEADS.basic}–${PLAN_INCLUDED_LEADS.premium}` },
          ]}
        />

        <Section title={t('seo.whyBuy', { trade: tradeName, place: page.place })}>
          <p>{answer}</p>
          <TrustStrip />
        </Section>

        <Section title={t('seo.membershipOptions')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {PLAN_CODES.map((code) => (
              <div key={code} className="rounded-2xl border border-slate-200 bg-white p-5">
                <h3 className="text-sm font-black uppercase tracking-wide text-slate-500">{code}</h3>
                <p className="mt-1 text-2xl font-black text-slate-900">
                  {money(PLAN_PRICES[code])}
                  <span className="text-sm font-semibold text-slate-500">{planBillingSuffix(code, lang)}</span>
                </p>

                <p className="mt-1 text-sm text-slate-600">
                  {t('seo.includedLeads', { n: PLAN_INCLUDED_LEADS[code] })}
                </p>
                <Link
                  to={path('/pricing')}
                  className="mt-3 inline-block rounded-lg bg-slate-900 px-4 py-2 text-xs font-bold text-white hover:bg-slate-800"
                >
                  {t('seo.viewPlans')}
                </Link>
              </div>
            ))}
          </div>
        </Section>

        <LiveProjects trade={page.trade.marketplaceTrade} heading={t('seo.openProjects', { trade: tradeName })} />

        <FaqSection faqs={faqs} />

        <LinkGrid title={t('seo.sameTradeOther')} links={related} icon="map" />
        <LinkGrid title={t('seo.otherTradesPlace', { place: page.place })} links={siblings} icon="trade" />
        <LinkGrid title={t('seo.relatedPages')} links={locationLinks} columns={2} />

        <CtaBand />
      </PageWrap>
    </>
  );
};

export default MoneyPage;
